/**
 * @fileoverview Константы для работы с системой mention
 * @description Централизованное хранение всех констант, связанных с mention-механизмом
 */

/**
 * Типы mention узлов
 * @readonly
 * @enum {string}
 */
export const MENTION_TYPES = {
  /** Обычное поле из внешнего сервиса */
  SERVICE: "service",
  /** Кастомное поле, определенное пользователем */
  CUSTOM: "custom",
  /** Поле с данными нотариуса */
  NOTARY: "notary",
  /** Число прописью (calc) */
  NUMBER_TO_WORDS: "number_to_words",
};

/**
 * Коды специальных сервисов
 * @readonly
 * @enum {string}
 */
export const SPECIAL_SERVICE_CODES = {
  /** Данные нотариуса */
  NOTARY: "notary",
  /** Ручной ввод данных */
  MANUAL_INPUT: "manual_input",
  /** Внутренние базы данных */
  INTERNAL_DATABASES: "internal_databases",
};

/**
 * Разделитель для формирования составных ключей mention
 * @constant {string}
 */
export const MENTION_KEY_SEPARATOR = "_";

/**
 * Префикс для отображения mention в документе
 * @constant {string}
 */
export const MENTION_DISPLAY_PREFIX = "$";

/**
 * Типы данных из ответов сервисов
 * @readonly
 * @enum {string}
 */
export const RESPONSE_DATA_TYPES = {
  PRIMITIVE: "primitive",
  OBJECT: "object",
  ARRAY: "array",
  DICTIONARY: "dictionary",
};

/**
 * Названия полей для определения справочников
 * @constant {string[]}
 */
export const DICTIONARY_FIELD_NAMES = ["name", "name_ru", "name_kz", "name_en"];

/**
 * Локали для локализации данных
 * @readonly
 * @enum {string}
 */
export const LOCALES = {
  RUSSIAN: "ru",
  KAZAKH: "kz",
  ENGLISH: "en",
};

/**
 * Режимы работы редактора
 * @readonly
 * @enum {string}
 */
export const EDITOR_MODES = {
  /** Режим редактирования */
  EDIT: "edit",
  /** Режим просмотра */
  VIEW: "view",
  /** Режим предпросмотра с подстановкой значений */
  PREVIEW: "preview",
};

/**
 * Канонические ключи, универсальные для ФЛ и ЮЛ
 * @constant {string[]}
 */
export const CANONICAL_KEYS = ["identifier", "display_name", "entity_address"];

/**
 * Нормализованный service_code для канонических полей
 * @constant {string}
 */
export const CANONICAL_SERVICE_CODE = "canonical";

/**
 * Сервисы, для которых канонические ключи нормализуются в общий namespace
 * @constant {string[]}
 */
export const CANONICAL_SOURCE_SERVICE_CODES = [
  "GBDFL",
  "GBDFLBasic",
  "GBDUL",
  "GBDULBasic",
];

export function isCanonicalKey(key) {
  if (!key) return false;
  // Вложенные части адреса (entity_address_region и т.п.) тоже канонические
  return CANONICAL_KEYS.includes(key) || key.startsWith("entity_address_");
}

export function isCanonicalSource(service_code) {
  if (!service_code) return false;
  const upper = String(service_code).toUpperCase();
  return CANONICAL_SOURCE_SERVICE_CODES.some((c) => c.toUpperCase() === upper);
}

export function normalizeCanonicalServiceCode(service_code, key) {
  return isCanonicalKey(key) && isCanonicalSource(service_code)
    ? CANONICAL_SERVICE_CODE
    : service_code;
}

/**
 * Человекочитаемые названия сервисов для tooltip
 * @constant {Object.<string,string>}
 */
export const SERVICE_LABELS = {
  canonical: "Канонические данные ГБД",
  notary: "Нотариус",
  manual_input: "Ручной ввод",
  internal_databases: "Внутренние базы",
  gbdfl: "ГБД ФЛ",
  gbdflbasic: "ГБД ФЛ (базовый)",
  gbdul: "ГБД ЮЛ",
  gbdulbasic: "ГБД ЮЛ (базовый)",
};

export function getServiceLabel(service_code) {
  if (!service_code) return "—";
  return SERVICE_LABELS[String(service_code).toLowerCase()] || service_code;
}
